import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import alertService from '../../utils/alert';
import { CheckCircle, AlertCircle, RefreshCw, Key, Trash2, ExternalLink } from 'lucide-react';
import { Button } from '../../components/ui/Button';

function Section({ title, icon: Icon, children }) {
  return (
    <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
      <div className="flex items-center gap-2 px-5 py-4 border-b border-[#D8DDE6]">
        <Icon size={16} className="text-[#4285F4]" />
        <span className="text-[13px] font-bold text-[#2E2E2E]">{title}</span>
      </div>
      <div className="p-4 sm:p-6 space-y-4">{children}</div>
    </div>
  );
}

const INPUT = 'w-full border border-[#D8DDE6] rounded px-3 py-2 text-[13px] text-[#2E2E2E] focus:outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]';
const LABEL = 'text-[11px] font-semibold text-[#706E6B] block mb-1.5';

export default function GoogleAdsSettingsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [creds, setCreds] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [selecting, setSelecting] = useState(false);
  const [form, setForm] = useState({ developerToken: '', clientId: '', clientSecret: '', loginCustomerId: '' });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [credsRes, statusRes] = await Promise.all([
        api.get('/marketing/google-ads/credentials'),
        api.get('/marketing/google-ads/oauth/status'),
      ]);
      setCreds(credsRes.data);
      setStatus(statusRes.data);
      setForm((f) => ({ ...f, loginCustomerId: statusRes.data.loginCustomerId || '' }));
    } catch {
      // First-time setup — nothing saved yet is expected, not an error.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (searchParams.get('connected')) {
      alertService.toast('success', 'Google Ads connected');
      setSearchParams({}, { replace: true });
      load();
    } else if (searchParams.get('error')) {
      alertService.error('Connection Failed', searchParams.get('error'));
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams, load]);

  const saveCredentials = async () => {
    if (!form.developerToken.trim()) {
      alertService.error('Missing field', 'Developer token is required');
      return;
    }
    setSaving(true);
    try {
      await api.put('/marketing/google-ads/credentials', form);
      alertService.toast('success', 'Google Ads credentials saved');
      setForm((f) => ({ ...f, developerToken: '', clientId: '', clientSecret: '' }));
      load();
    } catch (err) {
      alertService.error('Save Failed', err.response?.data?.error || 'Could not save credentials.');
    } finally {
      setSaving(false);
    }
  };

  const connect = async () => {
    setConnecting(true);
    try {
      const { data } = await api.get('/marketing/google-ads/oauth/url');
      window.location.href = data.authUrl;
    } catch (err) {
      alertService.error('Connection Failed', err.response?.data?.error || 'Could not start Google OAuth.');
      setConnecting(false);
    }
  };

  const disconnect = async () => {
    if (!(await alertService.confirm('Disconnect?', 'Disconnect Google Ads? Your saved developer token stays configured — you can reconnect anytime.', 'danger', 'Disconnect'))) return;
    try {
      await api.delete('/marketing/google-ads/oauth/disconnect');
      alertService.toast('success', 'Google Ads disconnected');
      load();
    } catch (err) {
      alertService.error('Failed', err.response?.data?.error || 'Could not disconnect.');
    }
  };

  const selectAccount = async (customerId) => {
    setSelecting(true);
    try {
      await api.put('/marketing/google-ads/accounts/select', { customerId, loginCustomerId: form.loginCustomerId });
      alertService.toast('success', 'Google Ads account selected');
      load();
    } catch (err) {
      alertService.error('Failed', err.response?.data?.error || 'Could not select account.');
    } finally {
      setSelecting(false);
    }
  };

  if (loading) return <div className="text-center py-16 text-[13px] text-[#5C5C5C]">Loading…</div>;

  return (
    <div className="max-w-2xl space-y-5">
      <Section title="Developer Token & OAuth Client" icon={Key}>
        <p className="text-[12px] text-[#5C5C5C]">
          Apply for a Google Ads API developer token in your Google Ads manager account
          (Tools &amp; Settings → API Center), and create an OAuth client in Google Cloud Console
          if you don't want to use this platform's shared one.{' '}
          <a href="https://developers.google.com/google-ads/api/docs/get-started/dev-token" target="_blank" rel="noreferrer" className="text-[#4285F4] inline-flex items-center gap-1">
            Setup guide <ExternalLink size={11} />
          </a>
        </p>
        <div>
          <label className={LABEL}>Developer Token</label>
          <input type="password" className={INPUT} value={form.developerToken}
            onChange={(e) => setForm({ ...form, developerToken: e.target.value })}
            placeholder={creds?.configured ? '••••••••••• (saved — leave blank to keep)' : 'Paste your developer token'} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={LABEL}>OAuth Client ID (optional)</label>
            <input className={INPUT} value={form.clientId} onChange={(e) => setForm({ ...form, clientId: e.target.value })}
              placeholder={creds?.hasCustomClient ? '••••••••• (saved)' : 'Uses platform default if blank'} />
          </div>
          <div>
            <label className={LABEL}>OAuth Client Secret (optional)</label>
            <input type="password" className={INPUT} value={form.clientSecret} onChange={(e) => setForm({ ...form, clientSecret: e.target.value })}
              placeholder={creds?.hasCustomClient ? '••••••••• (saved)' : 'Uses platform default if blank'} />
          </div>
        </div>
        <div>
          <label className={LABEL}>Manager (MCC) Customer ID — optional</label>
          <input className={INPUT} value={form.loginCustomerId} onChange={(e) => setForm({ ...form, loginCustomerId: e.target.value })}
            placeholder="e.g. 123-456-7890 — only if you access accounts through a manager account" />
        </div>
        <Button onClick={saveCredentials} loading={saving}>Save</Button>
      </Section>

      <Section title="Connection" icon={status?.connected ? CheckCircle : AlertCircle}>
        {status?.connected ? (
          <>
            <div className="flex items-center justify-between p-4 bg-[#F4F6F9] border border-[#D8DDE6] rounded-[4px]">
              <div>
                <p className="text-[14px] font-bold text-[#2E2E2E]">Connected</p>
                <p className="text-[12px] text-[#5C5C5C]">{status.accessibleCustomers?.length || 0} accessible account(s)</p>
              </div>
              <Button variant="outline" onClick={disconnect} className="text-[#BA0517] border-[#F6A3AB] hover:bg-[#FDE7E9]">
                <Trash2 className="w-4 h-4 mr-2" />
                Disconnect
              </Button>
            </div>
            {(status.accessibleCustomers || []).length > 0 && (
              <div className="space-y-2">
                <label className={LABEL}>Select an account to manage</label>
                {status.accessibleCustomers.map((c) => (
                  <div key={c.customerId} className={`flex items-center justify-between p-3 border rounded-[4px] ${status.selectedCustomerId === c.customerId ? 'border-[#4285F4] bg-[#EAF4FE]' : 'border-[#D8DDE6]'}`}>
                    <div>
                      <p className="text-[13px] font-medium text-[#2E2E2E]">{c.descriptiveName}{c.isManager ? ' (Manager)' : ''}</p>
                      <p className="text-[11px] text-[#5C5C5C]">{c.customerId} · {c.currencyCode || '—'}</p>
                    </div>
                    {status.selectedCustomerId === c.customerId ? (
                      <span className="text-[11px] font-bold text-[#4285F4]">Selected</span>
                    ) : (
                      <Button variant="outline" onClick={() => selectAccount(c.customerId)} loading={selecting} disabled={c.isManager}>
                        {c.isManager ? 'Manager only' : 'Select'}
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <>
            {!creds?.configured && (
              <div className="flex items-start gap-2 p-3 bg-[#FFF4E5] border border-[#FFB75D] rounded-[4px] text-[12px] text-[#8C4B02]">
                <AlertCircle size={15} className="shrink-0 mt-0.5" />
                <span>Save your developer token above before connecting.</span>
              </div>
            )}
            <Button onClick={connect} loading={connecting} disabled={!creds?.configured}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Connect Google Ads
            </Button>
          </>
        )}
      </Section>
    </div>
  );
}
